import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { AppProvider, useAppContext } from "@/context/AppContext";
import { NavigationProvider, useNavigation } from "@/context/NavigationContext";

import Navbar from "@/components/Navbar";
import InstrumentLoader from "@/components/InstrumentLoader";
import MaintenanceAlert from "@/components/MaintenanceAlert"; // 🟢 Importul alertei
import React, { useState, useEffect } from "react"; // 🟢 Importuri React necesare pentru stare

import Home from "./pages/Home";
import AircraftList from "./pages/AircraftList";
import AircraftRentalList from "./pages/AircraftRentalList";
import AircraftPartList from "./pages/AircraftPartList";
import ExperienceList from "./pages/ExperienceList";
import AircraftProductDetails from "./pages/AircraftProductDetails";
import RentalProductDetails from "./pages/RentalProductDetails";
import AircraftPartDetails from "./pages/AircraftPartDetails";
import ExperienceDetails from "./pages/ExperienceDetails";
import ProductDetails from "./pages/ProductDetails";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TermsConditions from "./pages/TermsConditions";
import SellerDashboard from "./pages/SellerDashboard";
import Cart from "./pages/Cart";
import AircraftListing from "./pages/AircraftListing";
import AircraftRental from "./pages/AircraftRental";
import AircraftPart from "./pages/AircraftPart";
import Experience from "./pages/Experience";
import ListingCategoryForm from "./components/ListingCategoryForm";
import NotFound from "./pages/NotFound";
import NotificationPanel from "./components/NotificationPanel";
import MyAccount from "./pages/MyAccount";
import MyOrders from "./pages/MyOrders";

const queryClient = new QueryClient();

const NavigationContent = () => {
  const { page } = useNavigation();

  const { loading: isSystemInitializing } = useAppContext();

  // 🟢 Extragem ambele flag-uri din useAuth()
  const { isMaintenanceBlock, isMaintenanceDev, isAuthLoading } = useAuth();
  const [isMaintenanceOpen, setIsMaintenanceOpen] = useState(false);

  useEffect(() => {
    // Alerta se deschide dacă cel puțin unul dintre modurile de mentenanță este activ
    if (!isAuthLoading && (isMaintenanceBlock || isMaintenanceDev)) {
      setIsMaintenanceOpen(true);
    }
  }, [isAuthLoading, isMaintenanceBlock, isMaintenanceDev]);

  const renderPage = () => {
    switch (page) {
      case "home": return <Home />;
      case "contact": return <Home />;
      case "aircraft": return <AircraftList />;
      case "rental": return <AircraftRentalList />;
      case "part": return <AircraftPartList />;
      case "experience": return <ExperienceList />;
      case "aircraft-details": return <AircraftProductDetails />;
      case "rental-details": return <RentalProductDetails />;
      case "part-details": return <AircraftPartDetails />;
      case "experience-details": return <ExperienceDetails />;
      case "product-details": return <ProductDetails />;
      case "login": return <Login />;
      case "register": return <Register />;
      case "terms-conditions": return <TermsConditions />;
      case "dashboard": return <SellerDashboard />;
      case "cart": return <Cart />;
      case "list-category": return <ListingCategoryForm />;
      case "list-aircraft": return <AircraftListing />;
      case "rent-aircraft": return <AircraftRental />;
      case "list-parts": return <AircraftPart />;
      case "list-experience": return <Experience />;
      case "notification-panel": return <NotificationPanel />;
      case "my-account": return <MyAccount />;
      case "my-orders": return <MyOrders />;
      default: return <NotFound />;
    }
  };

  return (
    <>
      <InstrumentLoader visible={isSystemInitializing} />
      <Navbar />
      <main className={`min-h-[calc(100vh-64px)] transition-opacity duration-300 ${
        isSystemInitializing ? "opacity-20 pointer-events-none" : "opacity-100"
      }`}>
        {renderPage()}
      </main>

      {/* 🟢 Trimitem isClosable ca fiind false DOAR dacă este activ block-ul critic.
        Dacă isMaintenanceBlock este true, !isMaintenanceBlock devine false (ascunde butonul).
      */}
      <MaintenanceAlert
        isOpen={isMaintenanceOpen}
        isClosable={!isMaintenanceBlock}
        onClose={() => setIsMaintenanceOpen(false)}
      />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <NavigationProvider>
          <CartProvider>
            <AppProvider>
              <Toaster />
              <Sonner />
              <NavigationContent />
            </AppProvider>
          </CartProvider>
        </NavigationProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;